import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { parseServerDate } from '../../utils/formatDate';

export default function AdminUsers({ showToast }) {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [editing, setEditing] = useState(null); // { id, plan, credits }

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (planFilter) params.set('plan', planFilter);
    authFetch(`/api/admin/users?${params}`).then(r => r.json()).then(d => { setUsers(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [planFilter]);

  const updateUser = async () => {
    if (!editing) return;
    const res = await authFetch(`/api/admin/users/${editing.id}/plan`, {
      method: 'PUT', body: JSON.stringify({ plan: editing.plan, credits: parseInt(editing.credits) })
    });
    if (res.ok) { showToast('User updated'); setEditing(null); load(); }
    else showToast('Update failed', 'error');
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Permanently delete user "${email}"? This cannot be undone.`)) return;
    const res = await authFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
    if (res.ok) { showToast('User deleted'); load(); }
    else showToast('Delete failed', 'error');
  };

  const planColor = p => p === 'enterprise' ? 'var(--accent2)' : p === 'pro' ? 'var(--accent)' : 'var(--text3)';

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="page-title">User Management</div>
          <span className="admin-sidebar-badge">ADMIN</span>
        </div>
        <div className="page-sub">Manage all platform users — plans, credits, access</div>
      </div>

      {/* Summary */}
      <div className="g4" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Users', val: users.length, color: 'var(--text)' },
          { label: 'Free', val: users.filter(u => u.plan === 'free').length, color: 'var(--text3)' },
          { label: 'Pro', val: users.filter(u => u.plan === 'pro').length, color: 'var(--gold-2)' },
          { label: 'Enterprise', val: users.filter(u => u.plan === 'enterprise').length, color: 'var(--accent2)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontFamily: 'var(--ff-m)', fontSize: 28, fontWeight: 500, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 280 }} placeholder="Search by name or email..."
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()} />
        <select className="form-select" style={{ maxWidth: 160 }} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <button className="btn btn-primary btn-sm" onClick={load}>Search</button>
      </div>

      {/* Edit modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 360, padding: 28 }}>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Edit User</div>
            <div className="form-group">
              <label className="form-label">Plan</label>
              <select className="form-select" value={editing.plan} onChange={e => setEditing({ ...editing, plan: e.target.value })}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">AI Credits</label>
              <input className="form-input" type="number" min={0} value={editing.credits}
                onChange={e => setEditing({ ...editing, credits: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={updateUser}>Save Changes</button>
              <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Plan</th>
                <th>Credits</th>
                <th>Companies</th>
                <th>Last Login</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No users found</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{u.name}</div>
                    <div style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{u.email}</div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{u.company || '—'}</td>
                  <td>
                    <span className={`badge plan-${u.plan}`} style={{ fontSize: 10 }}>● {u.plan?.toUpperCase()}</span>
                  </td>
                  <td>
                    <span className="credit-badge">⚡{u.credits}</span>
                  </td>
                  <td>
                    <span style={{ fontFamily: 'var(--ff-m)', color: 'var(--gold-2)', fontSize: 14 }}>{u.company_count || 0}</span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN') : 'Never'}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {parseServerDate(u.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                        onClick={() => setEditing({ id: u.id, plan: u.plan, credits: u.credits })}>
                        ✏️ Edit
                      </button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }}
                        onClick={() => deleteUser(u.id, u.email)}>
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--ff-m)' }}>
        {users.length} user{users.length !== 1 ? 's' : ''} shown
      </div>
    </div>
  );
}
